/**
 * AppLayoutRoute — the application shell.
 *
 * This is a ROUTE, not a wrapper component, and the distinction matters:
 *
 *   <Route element={<AppLayoutRoute />}>      ← mounts once
 *     <Route path="/" element={<Home />} />   ← swaps inside <Outlet />
 *   </Route>
 *
 * Because the shell mounts once and survives navigation, anything it holds
 * survives too: scroll position, an open menu, a websocket, a data cache. A
 * wrapper component re-mounts on every route change and loses all of it.
 *
 * ── Why this file exists rather than a <PageLayout> from the library ──
 *
 * UIKit 4.0 removed its layout components. Not because they were broken —
 * because every application built on them replaced them within weeks. App
 * chrome is where product identity lives, and a generic header is the first
 * thing anyone rewrites. So the shell is yours: roughly 90 lines, all of it
 * readable, none of it fighting a library's assumptions.
 *
 * Pages render through <Outlet /> and own their own <PageHeader>. Do not add
 * a page title here — the page knows its title, the shell does not.
 *
 * ── Home, Services, Work and About are full-bleed ─────────────────────────
 * Each paints a fixed WebGL canvas behind full-height sections, so they get
 * <main> with no column. Every other page is prose and gets the centred one. The header is translucent and the footer opaque for the same
 * reason: the canvas shows through the first and must not through the second.
 *
 * ── Auth ──────────────────────────────────────────────────────────────────
 * This is a studio site, so the header sells rather than signs in. The
 * session-dependent link (Sign in, or Dashboard once signed in) lives in the
 * footer's small print. Nothing is rendered there while the session resolves,
 * so it never flashes "Sign in" at someone already signed in.
 */
import * as React from 'react';
import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import { useTheme } from '@bloomneo/uikit';
import { brand } from './brand';
import { AnimatedMark, Lockup } from './SiteChrome';
import { launchRocket } from './rocketLaunch';
import { HeaderRobot, type HeaderRobotHandle } from './HeaderRobot';
import { RoboChat } from './RoboChat';
import { useAuth } from '@/features/auth/hooks';

const column = 'mx-auto w-full max-w-[1440px] px-[clamp(20px,4vw,64px)]';
const cta = 'rounded-full bg-brand font-semibold text-white transition-colors hover:bg-brand-hover';
/** Header type: the headline face (Big Shoulders Display), set in caps. */
const headerFont = 'font-display font-bold uppercase tracking-[.08em]';

const HINT_KEY = 'voilacode-robo-hint';

/** What Charlie says shortly after each page opens. */
const PAGE_HINTS: Record<string, string> = {
  '/': 'Hi, I\'m Charlie! Need help? Tap me 👋',
  '/services': 'Not sure which service fits? Tap me',
  '/work': 'Want something like this built? Tap me',
  '/about': 'Questions about us? Tap me and ask',
  '/contact': 'I can pass your message to the team — tap me',
};

/** And what it says, in turn, while the visitor is idle. */
const IDLE_HINTS = [
  'Need a hand? Tap me 👋',
  'Have a project in mind? Tap me',
  'Lost? Tap me and I\'ll guide you',
];


function NavLinks({ className, onNavigate }: { className: string; onNavigate?: () => void }) {
  return (
    <>
      {/* Home lives in the header only; `end` so it is not "active" on every page. */}
      <NavLink
        to="/"
        end
        onClick={onNavigate}
        className={({ isActive }) => `${className} ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
      >
        Home
      </NavLink>
      {brand.nav.map((item) =>
        // A hash link is never "the current page", so it is a plain Link:
        // NavLink would mark both of them active whenever you are on `/`.
        item.to.includes('#') ? (
          <Link key={item.to} to={item.to} onClick={onNavigate} className={`${className} text-muted-foreground`}>
            {item.label}
          </Link>
        ) : (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) => `${className} ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
          >
            {item.label}
          </NavLink>
        ),
      )}
    </>
  );
}

/** Staff entry to the content dashboard: sign-in first when signed out. */
function AdminLink() {
  const { isAuthenticated } = useAuth();
  return (
    <Link to={isAuthenticated ? '/dashboard' : '/auth/login?next=/dashboard'} className="hover:text-foreground">
      Admin
    </Link>
  );
}

/**
 * The arrow perched on the footer's top edge. Pressing it does not just jump:
 * a rocket assembles on it out of dots and carries the page back to the top.
 * Disabled for the duration so a second press cannot start a second launch.
 */
function BackToTop() {
  const [launching, setLaunching] = React.useState(false);
  return (
    <button
      type="button"
      aria-label="Back to top"
      disabled={launching}
      onClick={(e) => {
        setLaunching(true);
        void launchRocket(e.currentTarget).then(() => setLaunching(false));
      }}
      title="Back to top"
      className="vc-totop absolute -top-7 right-[clamp(20px,4vw,64px)] grid size-14 cursor-pointer place-items-center rounded-full border border-primary bg-primary text-2xl font-semibold text-primary-foreground shadow-[0_10px_30px_-8px_var(--color-primary)] transition-transform hover:scale-110 active:scale-95 disabled:cursor-default disabled:opacity-60"
    >
      <span aria-hidden>↑</span>
    </button>
  );
}

export function AppLayoutRoute() {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const { mode, setMode } = useTheme();
  const { pathname, hash } = useLocation();
  const fullBleed = ['/', '/services', '/work', '/about', '/contact'].includes(pathname);
  const robot = React.useRef<HeaderRobotHandle>(null);
  const [chatOpen, setChatOpen] = React.useState(false);
  /*
   * Charlie's nudges. Nobody taps a mascot they do not know is a button, so it
   * says so: once shortly after each page opens, in words that fit that page,
   * and again every so often while the visitor is idle. Each one shows for a
   * few seconds and the robot looks up as it speaks.
   *
   * The per-page prompt always shows, even after the chat has been used: it
   * was once suppressed for the rest of the visit after a single tap, and the
   * result was a robot that looked like it had stopped working. Only the idle
   * reminders and the beacon go quiet once someone has opened the chat.
   */
  const [hint, setHint] = React.useState<string | null>(null);
  const [chatUsed, setChatUsed] = React.useState(() => {
    try { return sessionStorage.getItem(HINT_KEY) === '1'; } catch { return false; }
  });
  React.useEffect(() => {
    if (chatOpen) return;
    let hide = 0;
    let turn = 0;
    const say = (text: string) => {
      setHint(text);
      robot.current?.look();
      window.clearTimeout(hide);
      hide = window.setTimeout(() => setHint(null), 9000);
    };
    // Services opens with a full-screen intro that covers the header; wait it out.
    const first = window.setTimeout(
      () => say(PAGE_HINTS[pathname] ?? PAGE_HINTS['/']),
      pathname === '/services' ? 9000 : 2500,
    );
    const again = chatUsed
      ? 0
      : window.setInterval(() => say(IDLE_HINTS[turn++ % IDLE_HINTS.length]), 40000);
    return () => {
      window.clearTimeout(first);
      window.clearTimeout(hide);
      window.clearInterval(again);
      setHint(null);
    };
  }, [pathname, chatUsed, chatOpen]);
  const openChat = () => {
    setChatUsed(true);
    try { sessionStorage.setItem(HINT_KEY, '1'); } catch { /* not remembered, no harm */ }
    setChatOpen((v) => !v);
  };
  // Pages can open the chat too: the Contact page's "Ask Charlie" link does.
  React.useEffect(() => {
    const open = () => {
      setChatUsed(true);
      try { sessionStorage.setItem(HINT_KEY, '1'); } catch { /* not remembered, no harm */ }
      robot.current?.look();
      setChatOpen(true);
    };
    window.addEventListener('charlie:open', open);
    return () => window.removeEventListener('charlie:open', open);
  }, []);
  const lookUp = React.useCallback(() => robot.current?.look(), []);
  const closeChat = React.useCallback(() => setChatOpen(false), []);
  // Home is 0; each nav page is one step further right, in nav order.
  const robotStep = brand.nav.findIndex((i) => pathname === i.to || pathname.startsWith(`${i.to}/`)) + 1;

  // The router does not scroll to hash targets; do it once the page is in.
  React.useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
  }, [pathname, hash]);

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      {/* Any link or button pressed in the header makes the robot look up.
          Capture phase, so it reacts before the mobile menu unmounts its links. */}
      <header
        className="sticky top-0 z-30 border-b border-border bg-nav backdrop-blur-md"
        onClickCapture={(e) => {
          if ((e.target as HTMLElement).closest('a,button')) robot.current?.look();
        }}
      >
        <div className={`${column} flex items-center justify-between gap-5 py-3.5`}>
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2.5 text-lg font-bold tracking-[-.01em]">
              <AnimatedMark />
              {brand.name}
            </Link>
            {/* Seated on the header's bottom edge: the wrapper only reserves the
                width, and the robot is pinned so it sits exactly on the border. */}
            <div className="relative h-10 w-[36px] shrink-0 min-[420px]:w-[62px]">
              <HeaderRobot
                ref={robot}
                step={robotStep}
                onClick={openChat}
                className="[--robo-step:3px] min-[420px]:[--robo-step:8px] min-[900px]:[--robo-step:18px] absolute -bottom-[14px] left-0 h-[50px] w-[36px] min-[420px]:h-16 min-[420px]:w-[46px] cursor-pointer rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {/* Until the chat has been opened once, a small beacon marks
                    the robot as something you can press. */}
                {!chatUsed && (
                  <span aria-hidden className="absolute right-0 top-1 flex size-2.5">
                    <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex size-2.5 rounded-full bg-primary" />
                  </span>
                )}
                {hint && !chatOpen && (
                  <span className="absolute left-1 top-[calc(100%+8px)] w-max max-w-[190px] rounded-2xl rounded-tl-sm bg-brand px-3 py-1.5 text-left font-sans text-xs font-medium normal-case leading-snug tracking-normal text-white shadow-lg transition-all duration-300 starting:translate-y-1 starting:opacity-0">
                    {hint}
                  </span>
                )}
              </HeaderRobot>
            </div>
          </div>

          <nav className={`${headerFont} hidden gap-[28px] text-[18px] min-[900px]:flex`} aria-label="Main">
            <NavLinks className="transition-colors hover:text-foreground" />
          </nav>

          <div className="flex items-center gap-2.5">
            <button
              type="button"
              aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
              className={`${headerFont} flex h-10 cursor-pointer items-center gap-2 rounded-full border border-line px-3.5 text-[15px] transition-colors hover:border-primary`}
            >
              <span aria-hidden className="text-[15px]">{mode === 'dark' ? '☀' : '☾'}</span>
              <span className="max-[419px]:hidden">{mode === 'dark' ? 'Light' : 'Dark'}</span>
            </button>
            <Link to="/contact" className={`${cta} ${headerFont} hidden px-[18px] py-2 text-[17px] min-[900px]:block`}>
              Start a project
            </Link>
            <button
              type="button"
              aria-label="Menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
              className="size-11 cursor-pointer rounded-xl border border-line font-mono text-lg min-[900px]:hidden"
            >
              {menuOpen ? '✕' : '≡'}
            </button>
          </div>
        </div>

        {/* The same links, stacked, below 900px. One source, two layouts. */}
        {menuOpen && (
          <nav className={`${headerFont} flex flex-col border-t border-border px-5 pb-6 pt-2 min-[900px]:hidden`} aria-label="Mobile">
            <NavLinks
              className="border-b border-border py-3.5 text-[26px]"
              onNavigate={() => setMenuOpen(false)}
            />
            <Link
              to="/contact"
              onClick={() => setMenuOpen(false)}
              className={`${cta} mt-5 px-[18px] py-3.5 text-center text-xl`}
            >
              Start a project
            </Link>
          </nav>
        )}
      </header>

      <RoboChat open={chatOpen} onClose={closeChat} onSpeak={lookUp} />

      {/* Pages render here. They return plain content and own their header. */}
      <main className={fullBleed ? 'flex-1' : 'mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6 sm:py-10'}>
        <Outlet />
      </main>

      <footer className="relative z-[2] border-t border-border bg-background text-muted-foreground">
        <BackToTop />
        <div className={`${column} grid grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))] gap-10 pb-8 pt-16`}>
          <div className="flex flex-col gap-3.5">
            <Link to="/" aria-label={`${brand.name} home`} className="w-fit">
              <Lockup className="h-36" />
            </Link>
            <div className="text-sm leading-[1.6]">{brand.tagline}</div>
          </div>
          <div className="flex flex-col gap-2.5 text-sm">
            <h2 className="font-mono text-xs font-normal tracking-normal text-subtle">STUDIO</h2>
            {brand.nav
              .filter((item) => item.to !== '/contact')
              .map((item) => (
                <Link key={item.to} to={item.to} className="transition-colors hover:text-foreground">
                  {item.label}
                </Link>
              ))}
          </div>
          <div className="flex flex-col gap-2.5 text-sm">
            <h2 className="font-mono text-xs font-normal tracking-normal text-subtle">CONTACT</h2>
            <a href={`mailto:${brand.email}`} className="text-foreground transition-colors hover:text-primary">
              {brand.email}
            </a>
            <Link to="/contact" className="transition-colors hover:text-foreground">
              Start a project →
            </Link>
            <span>{brand.address}</span>
          </div>
        </div>
        <div className={`${column} flex flex-wrap justify-between gap-4 border-t border-border py-6 text-[13px] text-subtle`}>
          <span>© {new Date().getFullYear()} {brand.legalName}</span>
          <span className="flex flex-wrap gap-x-4 gap-y-1">
            {brand.legal.map((l) => (
              <React.Fragment key={l.to}>
                <Link to={l.to} className="hover:text-foreground">
                  {l.label}
                </Link>
                {l.to === '/terms' && <AdminLink />}
              </React.Fragment>
            ))}
          </span>
          <span className="font-mono text-xs">{brand.trust.join(' · ')}</span>
        </div>
      </footer>
    </div>
  );
}
